<template>
  <div class="labs-specimens">
    <div class="labs-section-header">
      <h3>Specimens</h3>
      <div class="labs-header-actions">
        <input
          type="text"
          v-model="searchText"
          placeholder="Search specimens..."
          class="labs-search-input"
        />
      </div>
    </div>

    <div v-if="loading" class="labs-loading">Loading specimens...</div>

    <div v-else-if="filteredSpecimens.length === 0" class="labs-empty">
      No specimens found.
    </div>

    <div v-else class="labs-table-wrapper">
      <table class="labs-table">
        <thead>
          <tr>
            <th class="col-visible">Visible</th>
            <th class="col-test">Test #</th>
            <th class="col-sample">Sample #</th>
            <th class="col-type">Test Type</th>
            <th class="col-date">Test Date</th>
            <th class="col-cured">Days Cured</th>
            <th class="col-wbm">WBM %</th>
            <th class="col-break">Break (kgf)</th>
            <th class="col-axial">Axial (MPa)</th>
            <th class="col-tensile">Split Tensile (MPa)</th>
            <th class="col-tumble">Tumble Index</th>
            <th class="col-drop">Drop Test</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="spec in paginatedSpecimens"
            :key="spec.id"
            :class="{ 'row-hidden': !spec.visibleToCustomer }"
          >
            <td class="col-visible">
              <label class="labs-toggle" v-if="canToggleVisibility">
                <input
                  type="checkbox"
                  :checked="spec.visibleToCustomer"
                  @change="toggleVisibility(spec)"
                />
                <span class="labs-toggle-slider"></span>
              </label>
              <span v-else class="labs-visibility-badge" :class="spec.visibleToCustomer ? 'visible' : 'hidden'">
                {{ spec.visibleToCustomer ? 'Yes' : 'No' }}
              </span>
            </td>
            <td class="col-test">{{ spec.testNumber || '—' }}</td>
            <td class="col-sample">{{ spec.sampleNumber || '—' }}</td>
            <td class="col-type">{{ spec.testType || '—' }}</td>
            <td class="col-date">{{ formatDate(spec.testDate) }}</td>
            <td class="col-cured">{{ formatDecimal(spec.totalDaysCured) }}</td>
            <td class="col-wbm">{{ formatDecimal(spec.measuredWbmPct) }}</td>
            <td class="col-break">{{ formatDecimal(spec.singlePointBreakKgf) }}</td>
            <td class="col-axial">{{ formatDecimal(spec.axialCompressionMpa) }}</td>
            <td class="col-tensile">{{ formatDecimal(spec.splitTensileMpa) }}</td>
            <td class="col-tumble">{{ formatDecimal(spec.tumbleIndexIso3271) }}</td>
            <td class="col-drop">{{ formatDecimal(spec.dropTestCpft5Mm) }}</td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="labs-pagination" v-if="totalPages > 1">
        <button @click="currentPage = 1" :disabled="currentPage === 1">&laquo;</button>
        <button @click="currentPage--" :disabled="currentPage === 1">&lsaquo;</button>
        <span class="labs-page-info">Page {{ currentPage }} of {{ totalPages }}</span>
        <button @click="currentPage++" :disabled="currentPage === totalPages">&rsaquo;</button>
        <button @click="currentPage = totalPages" :disabled="currentPage === totalPages">&raquo;</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'Specimens',
  props: {
    labsCustomerId: { type: Number, required: true },
    apiBase: { type: String, required: true },
    canToggleVisibility: { type: Boolean, default: false }
  },
  data() {
    return {
      specimens: [],
      searchText: '',
      loading: false,
      currentPage: 1,
      pageSize: 20
    };
  },
  computed: {
    filteredSpecimens() {
      if (!this.searchText.trim()) return this.specimens;
      const q = this.searchText.toLowerCase();
      return this.specimens.filter(s =>
        (s.testNumber && s.testNumber.toLowerCase().includes(q)) ||
        (s.sampleNumber && s.sampleNumber.toLowerCase().includes(q)) ||
        (s.testType && s.testType.toLowerCase().includes(q)) ||
        (s.comments && s.comments.toLowerCase().includes(q))
      );
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredSpecimens.length / this.pageSize));
    },
    paginatedSpecimens() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredSpecimens.slice(start, start + this.pageSize);
    }
  },
  watch: {
    labsCustomerId: 'loadSpecimens',
    searchText() { this.currentPage = 1; }
  },
  created() {
    this.loadSpecimens();
  },
  methods: {
    async loadSpecimens() {
      if (!this.labsCustomerId) return;
      this.loading = true;
      try {
        const resp = await axios.get(
          `${this.apiBase}/api/labs/customers/${this.labsCustomerId}/specimens`
        );
        this.specimens = resp.data || [];
      } catch (err) {
        console.error('Failed to load specimens:', err);
        this.specimens = [];
      } finally {
        this.loading = false;
      }
    },

    async toggleVisibility(spec) {
      const newVal = !spec.visibleToCustomer;
      try {
        await axios.patch(
          `${this.apiBase}/api/labs/specimens/${spec.id}/visibility`,
          { visible: newVal }
        );
        spec.visibleToCustomer = newVal;
      } catch (err) {
        console.error('Failed to toggle specimen visibility:', err);
      }
    },

    formatDate(val) {
      if (!val) return '—';
      return new Date(val).toLocaleDateString();
    },

    formatDecimal(val) {
      if (val == null) return '—';
      return Number(val).toLocaleString(undefined, { maximumFractionDigits: 2 });
    }
  }
};
</script>

<style>
/* Specimens — non-scoped so styles survive builds */
.labs-specimens .labs-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.labs-specimens .labs-section-header h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #1a1a1a;
}
.labs-specimens .labs-search-input {
  padding: 6px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.875rem;
  width: 220px;
}
.labs-specimens .labs-table-wrapper {
  overflow-x: auto;
}
.labs-specimens .labs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}
.labs-specimens .labs-table th,
.labs-specimens .labs-table td {
  padding: 8px 10px;
  text-align: left;
  border-bottom: 1px solid #e5e5e5;
  white-space: nowrap;
}
.labs-specimens .labs-table thead th {
  background: #f5f6f8;
  font-weight: 600;
  color: #555;
  position: sticky;
  top: 0;
  z-index: 1;
}
.labs-specimens .labs-table tbody tr:hover {
  background: #f9fafb;
}
.labs-specimens .row-hidden {
  opacity: 0.55;
}

/* Toggle switch */
.labs-specimens .labs-toggle {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
}
.labs-specimens .labs-toggle input { opacity: 0; width: 0; height: 0; }
.labs-specimens .labs-toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #ccc;
  border-radius: 20px;
  transition: 0.2s;
}
.labs-specimens .labs-toggle-slider::before {
  content: '';
  position: absolute;
  width: 16px;
  height: 16px;
  left: 2px;
  bottom: 2px;
  background: #fff;
  border-radius: 50%;
  transition: 0.2s;
}
.labs-specimens .labs-toggle input:checked + .labs-toggle-slider {
  background: #28a745;
}
.labs-specimens .labs-toggle input:checked + .labs-toggle-slider::before {
  transform: translateX(16px);
}
.labs-specimens .labs-visibility-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 8px;
}
.labs-specimens .labs-visibility-badge.visible { background: #d4edda; color: #155724; }
.labs-specimens .labs-visibility-badge.hidden  { background: #e9ecef; color: #6c757d; }

/* Pagination */
.labs-specimens .labs-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 12px;
  padding: 8px 0;
}
.labs-specimens .labs-pagination button {
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 4px 10px;
  cursor: pointer;
  font-size: 0.85rem;
  color: #333;
}
.labs-specimens .labs-pagination button:disabled {
  opacity: 0.4;
  cursor: default;
}
.labs-specimens .labs-pagination button:not(:disabled):hover {
  background: #f0f0f0;
}
.labs-specimens .labs-page-info {
  font-size: 0.85rem;
  color: #555;
  margin: 0 8px;
}

.labs-specimens .labs-loading,
.labs-specimens .labs-empty {
  padding: 24px;
  text-align: center;
  color: #888;
}
</style>
